/**
 * DATEV holds up to three hourly rates per employee, Stundenlohn 1, 2 and 3,
 * and the Lohnart of a booking decides which one values its hours. A slot is
 * that number: the contract stores one rate per slot, and a company assigns
 * each of its hour Lohnarten to one slot.
 *
 * Stundenlohn 1 is the rate the contract has always carried in
 * `remuneration.hourly`, so everything that reads that field keeps reading
 * the same rate. The others sit next to it as `hourly2` and `hourly3`, which
 * map one to one onto `std_lohn_2#psd` and `std_lohn_3#psd` in the LODAS
 * Stammdaten record.
 */
export const hourlyRateSlots = [1, 2, 3] as const
export type HourlyRateSlot = (typeof hourlyRateSlots)[number]

/** The slot a Lohnart uses when nobody assigned one: the contract rate. */
export const DEFAULT_HOURLY_RATE_SLOT: HourlyRateSlot = 1

const FIELD_BY_SLOT = {
  1: 'hourly',
  2: 'hourly2',
  3: 'hourly3',
} as const satisfies Record<HourlyRateSlot, string>

export type HourlyRateField = (typeof FIELD_BY_SLOT)[HourlyRateSlot]

/** The `remuneration` field a slot's rate is stored in. */
export const hourlyRateField = (slot: HourlyRateSlot): HourlyRateField =>
  FIELD_BY_SLOT[slot]

/** A stored slot, or Stundenlohn 1 for anything that is not one. */
export const hourlyRateSlotOf = (value: unknown): HourlyRateSlot =>
  (hourlyRateSlots as readonly unknown[]).includes(value)
    ? (value as HourlyRateSlot)
    : DEFAULT_HOURLY_RATE_SLOT

/**
 * The rate in euros a contract states for a slot, or null when it states none.
 *
 * 0 is no rate. DATEV exports an unused Stundenlohn as 0,00, and a booking
 * valued at that rate would pay nothing while looking imported.
 */
export const statedHourlyRate = (
  remuneration:
    | Partial<Record<HourlyRateField, number | null | undefined>>
    | null
    | undefined,
  slot: HourlyRateSlot
): number | null => {
  const value = remuneration?.[hourlyRateField(slot)]
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? value
    : null
}
